/**
 * Tenant Configuration
 *
 * This module provides centralized configuration for multi-tenant support.
 * Configuration is derived from the commerce kernel channel settings and can be
 * customized per tenant/store.
 */

export type SupportedLocale = 'ar-SA' | 'en-US' | 'en-GB';
export type SupportedCurrency = 'SAR' | 'AED' | 'KWD' | 'BHD' | 'OMR' | 'QAR' | 'USD' | 'EUR' | 'GBP';
type SupportedCountry = 'SA' | 'AE' | 'US' | 'GB';

export interface TenantConfig {
  /** Locale for formatting (dates, numbers) */
  locale: SupportedLocale;
  /** UI language code */
  language: 'ar' | 'en';
  /** Text direction */
  direction: 'rtl' | 'ltr';
  /** Default currency code */
  currency: SupportedCurrency;
  /** Default country code */
  country: SupportedCountry;
  /** Timezone for date display */
  timezone: string;
  /** Number formatting options */
  numberFormat: {
    minimumFractionDigits: number;
    maximumFractionDigits: number;
  };
  /** Date formatting options */
  dateFormat: {
    short: Intl.DateTimeFormatOptions;
    long: Intl.DateTimeFormatOptions;
    withTime: Intl.DateTimeFormatOptions;
  };
}

/** Currency symbol mapping */
export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  SAR: 'ر.س',
  AED: 'د.إ',
  KWD: 'د.ك',
  BHD: 'د.ب',
  OMR: 'ر.ع',
  QAR: 'ر.ق',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

/**
 * Currency decimal places (ISO 4217).
 * Determines how many fractional digits to display for each currency.
 * KWD, BHD, OMR use 3 decimals (fils); all others use 2.
 */
export const CURRENCY_DECIMALS: Record<SupportedCurrency, number> = {
  SAR: 2,
  AED: 2,
  KWD: 3,
  BHD: 3,
  OMR: 3,
  QAR: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
};

/** Default configuration for Saudi Arabia (Arabic) */
export const DEFAULT_CONFIG: TenantConfig = {
  locale: 'ar-SA',
  language: 'ar',
  direction: 'rtl',
  currency: 'SAR',
  country: 'SA',
  timezone: 'Asia/Riyadh',
  numberFormat: {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
  dateFormat: {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    },
    withTime: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
  },
};
